import React, { useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle, User, Building2, Briefcase, DollarSign, Calendar, FileText, Loader2, Save } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useDisputes } from '@/hooks/useDisputes';
import { useToast } from '@/hooks/use-toast';
import { Dispute, DisputeDetails, DisputeStatus } from '@/lib/types/completion';

export default function DisputeManagementPage() {
  const { disputes, loading, resolveDispute, fetchDisputeDetails, updateAdminNotes } = useDisputes();
  const { toast } = useToast();
  const [selectedDispute, setSelectedDispute] = useState<DisputeDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [resolution, setResolution] = useState<'resolved_for_client' | 'resolved_for_vendor' | ''>('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [processing, setProcessing] = useState(false);
  
  // Admin notes state
  const [adminNotes, setAdminNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [hasUnsavedNotes, setHasUnsavedNotes] = useState(false);

  const getStatusColor = (status: DisputeStatus) => {
    switch (status) {
      case 'open':
        return 'destructive';
      case 'under_review':
        return 'secondary';
      case 'resolved_for_client':
      case 'resolved_for_vendor':
        return 'default';
      default:
        return 'secondary';
    }
  };

  const getPriorityLevel = (dispute: Dispute) => {
    const daysSinceCreated = Math.floor(
      (Date.now() - new Date(dispute.created_at).getTime()) / (1000 * 60 * 60 * 24)
    );
    
    if (daysSinceCreated > 7) return 'high';
    if (daysSinceCreated > 3) return 'medium';
    return 'low';
  };

  const formatCurrency = (cents: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleOpenDispute = async (dispute: Dispute) => {
    setLoadingDetails(true);
    try {
      const details = await fetchDisputeDetails(dispute.id);
      if (details) {
        setSelectedDispute(details);
        setAdminNotes(details.admin_notes || '');
        setHasUnsavedNotes(false);
      }
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedDispute(null);
    setResolution('');
    setResolutionNotes('');
    setAdminNotes('');
    setHasUnsavedNotes(false);
  };

  const handleAdminNotesChange = (value: string) => {
    setAdminNotes(value);
    setHasUnsavedNotes(value !== (selectedDispute?.admin_notes || ''));
  };

  const handleSaveAdminNotes = async () => {
    if (!selectedDispute) return;
    
    setSavingNotes(true);
    try {
      await updateAdminNotes(selectedDispute.id, adminNotes);
      setHasUnsavedNotes(false);
      toast({
        title: "Notes saved",
        description: "Admin notes have been saved successfully.",
      });
    } catch (error) {
      toast({
        title: "Error saving notes",
        description: error instanceof Error ? error.message : 'Failed to save notes',
        variant: "destructive",
      });
    } finally {
      setSavingNotes(false);
    }
  };

  const handleResolveDispute = async () => {
    if (!selectedDispute || !resolution) return;

    try {
      setProcessing(true);
      
      await resolveDispute(selectedDispute.id, resolution, resolutionNotes);
      
      toast({
        title: "Dispute resolved",
        description: `The dispute has been resolved in favor of the ${resolution === 'resolved_for_client' ? 'client' : 'vendor'}.`,
      });

      handleCloseModal();
    } catch (error) {
      toast({
        title: "Error resolving dispute",
        description: error instanceof Error ? error.message : 'Failed to resolve dispute',
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const DisputeDetailModal = () => (
    <Dialog open={!!selectedDispute} onOpenChange={handleCloseModal}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">
              Dispute Resolution - {selectedDispute?.public_id}
            </DialogTitle>
            {selectedDispute && (
              <div className="flex items-center gap-2">
                <Badge variant={getStatusColor(selectedDispute.status)}>
                  {selectedDispute.status.replace(/_/g, ' ')}
                </Badge>
                <Badge variant={getPriorityLevel(selectedDispute) === 'high' ? 'destructive' : 'secondary'}>
                  {getPriorityLevel(selectedDispute)} priority
                </Badge>
              </div>
            )}
          </div>
        </DialogHeader>
        
        {selectedDispute && (
          <div className="space-y-6 mt-4">
            {/* Job Information */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Job Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-xs text-muted-foreground">Job Name</Label>
                    <p className="text-sm font-medium">{selectedDispute.job?.title || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Job Number</Label>
                    <p className="text-sm font-medium font-mono">{selectedDispute.job?.public_id || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Job Value</Label>
                    <p className="text-sm font-medium">
                      {selectedDispute.job?.budget_cents 
                        ? formatCurrency(selectedDispute.job.budget_cents) 
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Start Date</Label>
                    <p className="text-sm font-medium">{formatDate(selectedDispute.job?.estimated_start_date)}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Due Date</Label>
                    <p className="text-sm font-medium">{formatDate(selectedDispute.job?.due_date)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Parties Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Client
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Name</Label>
                    <p className="text-sm font-medium">
                      {selectedDispute.client?.first_name || selectedDispute.client?.last_name
                        ? `${selectedDispute.client?.first_name || ''} ${selectedDispute.client?.last_name || ''}`.trim()
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Organization</Label>
                    <p className="text-sm font-medium">
                      {selectedDispute.client?.organization_name || selectedDispute.client_organization?.name || '-'}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Building2 className="h-4 w-4" />
                    Vendor
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div>
                    <Label className="text-xs text-muted-foreground">Contact Name</Label>
                    <p className="text-sm font-medium">
                      {selectedDispute.vendor?.first_name || selectedDispute.vendor?.last_name
                        ? `${selectedDispute.vendor?.first_name || ''} ${selectedDispute.vendor?.last_name || ''}`.trim()
                        : '-'}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Business Name</Label>
                    <p className="text-sm font-medium">{selectedDispute.vendor?.business_name || '-'}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Dispute Details */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Dispute Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedDispute.dispute_type && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Dispute Type</Label>
                    <p className="text-sm font-medium capitalize">
                      {selectedDispute.dispute_type.replace(/_/g, ' ')}
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Reason</Label>
                  <p className="text-sm mt-1 whitespace-pre-wrap">{selectedDispute.reason}</p>
                </div>
                {selectedDispute.requested_resolution && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Requested Resolution</Label>
                    <p className="text-sm mt-1 whitespace-pre-wrap">{selectedDispute.requested_resolution}</p>
                  </div>
                )}
                {selectedDispute.supporting_documents && selectedDispute.supporting_documents.length > 0 && (
                  <div>
                    <Label className="text-xs text-muted-foreground">Supporting Documents</Label>
                    <p className="text-sm text-muted-foreground mt-1">
                      {selectedDispute.supporting_documents.length} document(s) attached
                    </p>
                  </div>
                )}
                <div>
                  <Label className="text-xs text-muted-foreground">Filed On</Label>
                  <p className="text-sm font-medium">{formatDate(selectedDispute.created_at)}</p>
                </div>
              </CardContent>
            </Card>

            {/* Admin Notes */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-base">Admin Notes</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Textarea
                  value={adminNotes}
                  onChange={(e) => handleAdminNotesChange(e.target.value)}
                  placeholder="Add investigation notes, findings, or any relevant observations..."
                  rows={4}
                  className="resize-none"
                />
                <div className="flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {hasUnsavedNotes && (
                      <span className="text-amber-600">Unsaved changes</span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleSaveAdminNotes}
                    disabled={!hasUnsavedNotes || savingNotes}
                  >
                    {savingNotes ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Notes
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Resolution Form */}
            {selectedDispute.status === 'open' && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Resolution Decision</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <RadioGroup
                    value={resolution}
                    onValueChange={(value) => setResolution(value as any)}
                  >
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="resolved_for_client" id="client" />
                      <Label htmlFor="client" className="flex items-center gap-2 cursor-pointer flex-1">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <div>
                          <p className="font-medium">Resolve in favor of Client</p>
                          <p className="text-xs text-muted-foreground">Client's claim is valid, vendor at fault</p>
                        </div>
                      </Label>
                    </div>
                    <div className="flex items-center space-x-2 p-3 border rounded-lg hover:bg-muted/50 cursor-pointer">
                      <RadioGroupItem value="resolved_for_vendor" id="vendor" />
                      <Label htmlFor="vendor" className="flex items-center gap-2 cursor-pointer flex-1">
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                        <div>
                          <p className="font-medium">Resolve in favor of Vendor</p>
                          <p className="text-xs text-muted-foreground">Vendor fulfilled obligations, claim is invalid</p>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>

                  <div>
                    <Label htmlFor="resolution-notes">Resolution Notes</Label>
                    <Textarea
                      id="resolution-notes"
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      placeholder="Explain the reasoning behind this resolution decision..."
                      rows={4}
                      className="mt-1"
                    />
                  </div>

                  <Separator />

                  <div className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      onClick={handleCloseModal}
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleResolveDispute}
                      disabled={!resolution || processing}
                    >
                      {processing ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Resolving...
                        </>
                      ) : (
                        'Resolve Dispute'
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Resolution History */}
            {selectedDispute.status !== 'open' && (
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-base">Resolution History</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-green-600" />
                      <span className="font-medium">
                        Resolved in favor of {selectedDispute.status === 'resolved_for_client' ? 'Client' : 'Vendor'}
                      </span>
                    </div>
                    {selectedDispute.resolved_at && (
                      <p className="text-sm text-muted-foreground">
                        Resolved on: {new Date(selectedDispute.resolved_at).toLocaleString()}
                      </p>
                    )}
                    {selectedDispute.resolution_notes && (
                      <div className="mt-3 p-3 bg-muted/50 rounded-lg">
                        <Label className="text-xs text-muted-foreground">Resolution Notes</Label>
                        <p className="text-sm mt-1 whitespace-pre-wrap">
                          {selectedDispute.resolution_notes}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="h-96 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const openDisputes = disputes.filter(d => d.status === 'open');
  const resolvedDisputes = disputes.filter(d => d.status.startsWith('resolved'));

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Dispute Management</h1>
        <p className="text-muted-foreground">
          Review and resolve disputes between clients and vendors
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{openDisputes.length}</div>
            <p className="text-sm text-muted-foreground">Open Disputes</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">{resolvedDisputes.length}</div>
            <p className="text-sm text-muted-foreground">Resolved</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {disputes.filter(d => getPriorityLevel(d) === 'high').length}
            </div>
            <p className="text-sm text-muted-foreground">High Priority</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="text-2xl font-bold">
              {Math.round((resolvedDisputes.length / Math.max(disputes.length, 1)) * 100)}%
            </div>
            <p className="text-sm text-muted-foreground">Resolution Rate</p>
          </CardContent>
        </Card>
      </div>

      {/* Disputes Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Disputes</CardTitle>
        </CardHeader>
        <CardContent>
          {disputes.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Dispute ID</TableHead>
                  <TableHead>Job</TableHead>
                  <TableHead>Created Date</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {disputes.map((dispute) => (
                  <TableRow key={dispute.id}>
                    <TableCell>
                      <Button
                        variant="link"
                        className="p-0 h-auto font-medium text-primary"
                        onClick={() => handleOpenDispute(dispute)}
                        disabled={loadingDetails}
                      >
                        {dispute.public_id}
                      </Button>
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {dispute.job?.public_id || '-'}
                    </TableCell>
                    <TableCell>
                      {new Date(dispute.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant={getPriorityLevel(dispute) === 'high' ? 'destructive' : 'secondary'}
                      >
                        {getPriorityLevel(dispute)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={getStatusColor(dispute.status)}>
                        {dispute.status.replace(/_/g, ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-xs truncate">
                      {dispute.reason}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenDispute(dispute)}
                        disabled={loadingDetails}
                      >
                        {dispute.status === 'open' ? 'Resolve' : 'View'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No disputes to review</p>
            </div>
          )}
        </CardContent>
      </Card>

      <DisputeDetailModal />
    </div>
  );
}